
/**
 * Trims leading zeros from a string representation of a number.
 * Ensures we don't strip the zero before a decimal (e.g., "0.5") or a lone "0".
 */
export const stripLeadingZerosString = (s: string): string => {
  if (!s) return "0";
  // Remove leading zeros but keep at least one digit (handle 0.xx)
  const stripped = s.replace(/^0+(?!\.|$)/, '');
  return stripped === "" ? "0" : stripped;
};

/**
 * Safely parses any value into a number, removing non-numeric formatting characters.
 */
export const parseNumberish = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;
  
  // Remove commas, dollar signs, and percent signs
  const clean = value.toString().replace(/[$,%\s]/g, "");
  const parsed = parseFloat(clean);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Formats a number as currency with EXACTLY 0 decimals ($1,234,567)
 * Used for high-level summary totals.
 */
export const formatCurrency0 = (n: number | string): string => {
  const val = typeof n === "string" ? parseNumberish(n) : n;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
};

/**
 * Formats a number as currency with EXACTLY 2 decimals ($1,234.56)
 * Standard for all detailed financial rows and inputs.
 */
export const formatCurrency2 = (n: number | string): string => {
  const val = typeof n === "string" ? parseNumberish(n) : n;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
};

/**
 * Formats a number with commas and EXACTLY 2 decimals (1,234.56)
 */
export const formatNumber2 = (n: number | string): string => {
  const val = typeof n === "string" ? parseNumberish(n) : n;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
};

/**
 * Formats a number as a percentage with EXACTLY 2 decimals (6.50%)
 */
export const formatPercent2 = (n: number | string | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(typeof n === "string" ? parseNumberish(n) : n)) return "N/A";
  const val = typeof n === "string" ? parseNumberish(n) : n;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val) + "%";
};
